import { Bot, ChevronDown, ChevronUp, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { SearchResponse } from "../lib/documentSearch/types";

type AgentMessageProps = {
  response: SearchResponse | null;
  isSearching: boolean;
  onFeedback?: (
    label: "good_training_example" | "needs_adapter_fix",
    comment?: string,
  ) => void;
};

export function AgentMessage({ response, isSearching, onFeedback }: AgentMessageProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [feedback, setFeedback] = useState<"good_training_example" | "needs_adapter_fix" | null>(
    null,
  );
  const [showFeedbackComment, setShowFeedbackComment] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState("");
  const feedbackCommentRef = useRef<HTMLTextAreaElement | null>(null);
  const message = isSearching
    ? "Checking the document index for related sources."
    : response
      ? response.answer ??
        (response.mode === "manifest-fallback"
          ? `AI Search is unavailable; showing ${response.total} local manifest match${
              response.total === 1 ? "" : "es"
            }.`
          : response.mode === "local-mock"
            ? `Using local mock data; showing ${response.total} sample match${
                response.total === 1 ? "" : "es"
              }.`
            : `I found ${response.total} related source${
                response.total === 1 ? "" : "s"
              } for review.`)
      : "Ready to gather related source documents.";
  const messageKey = isSearching
    ? "searching"
    : response
      ? `${response.logId ?? ""}:${response.query}:${response.total}:${
          response.answer ?? response.mode ?? ""
        }`
      : "idle";
  const hasSummaryActions = isSearching || response !== null;

  useEffect(() => {
    setCollapsed(false);
    setDismissed(false);
    setFeedback(null);
    setShowFeedbackComment(false);
    setFeedbackComment("");
  }, [messageKey]);

  useEffect(() => {
    if (showFeedbackComment) {
      feedbackCommentRef.current?.focus();
    }
  }, [showFeedbackComment]);

  if (dismissed) {
    return null;
  }

  return (
    <div
      className={[
        "agent-message",
        collapsed ? "agent-message--collapsed" : "",
        isSearching ? "agent-message--searching" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-live="polite"
    >
      <div className="agent-message__avatar" aria-hidden="true">
        <Bot size={18} />
      </div>
      <div className="agent-message__body">
        <div className="agent-message__header">
          <div className="agent-message__title">
            <span>Source assistant</span>
          </div>
          {hasSummaryActions ? (
            <div className="agent-message__actions">
              <button
                className="small-icon-button"
                type="button"
                aria-expanded={!collapsed}
                title={collapsed ? "Expand summary" : "Collapse summary"}
                onClick={() => setCollapsed((current) => !current)}
              >
                {collapsed ? (
                  <ChevronDown size={15} aria-hidden="true" />
                ) : (
                  <ChevronUp size={15} aria-hidden="true" />
                )}
              </button>
              <button
                className="small-icon-button"
                type="button"
                title="Close summary"
                onClick={() => setDismissed(true)}
              >
                <X size={15} aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
        {collapsed ? null : (
          <>
            <p>{message}</p>
            {isSearching ? (
              <div
                className="search-progress"
                role="progressbar"
                aria-label="Searching document sources"
              >
                <span />
              </div>
            ) : null}
            {!isSearching && response && onFeedback ? (
              <div className="agent-feedback-block">
                <div className="agent-feedback" aria-label="Rate assistant response">
                  <button
                    className={feedback === "good_training_example" ? "is-active" : ""}
                    type="button"
                    title="Flag as good case"
                    aria-label="Flag as good case"
                    onClick={() => {
                      setFeedback("good_training_example");
                      setShowFeedbackComment(false);
                      setFeedbackComment("");
                      onFeedback("good_training_example");
                    }}
                  >
                    <ThumbsUp size={15} aria-hidden="true" />
                  </button>
                  <button
                    className={feedback === "needs_adapter_fix" ? "is-active" : ""}
                    type="button"
                    title="Flag as bad case"
                    aria-label="Flag as bad case"
                    onClick={() => {
                      setFeedback("needs_adapter_fix");
                      setShowFeedbackComment(true);
                    }}
                  >
                    <ThumbsDown size={15} aria-hidden="true" />
                  </button>
                </div>
                {showFeedbackComment ? (
                  <form
                    className="agent-feedback-note"
                    onSubmit={(event) => {
                      event.preventDefault();
                      onFeedback("needs_adapter_fix", feedbackComment.trim() || undefined);
                      setShowFeedbackComment(false);
                    }}
                  >
                    <textarea
                      ref={feedbackCommentRef}
                      aria-label="Feedback comment"
                      rows={2}
                      value={feedbackComment}
                      placeholder="What should be fixed?"
                      onChange={(event) => setFeedbackComment(event.target.value)}
                    />
                    <button className="secondary-button" type="submit">
                      Save
                    </button>
                  </form>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
